<template>
  <hierarchical-item-selector
    recursive
    v-model="value"
    v-bind:options="options"
    v-bind:disabled="disabled"
    v-bind:id-prefix="modifiedIdPrefix"
    v-bind:selected-all-label="selectedAllLabel"
    v-bind:get-selected-text="getSelectedText"
    v-bind:caption="
      $t(
        'appSensorGroupAndSensorSelector.message.selectIndividuallyExceptForAll'
      )
    "
    v-bind:size="size"
    v-on:change="$_onChange"
    v-on:selected="$_onSelected"
  >
    <template v-slot:item-label="{ item }">
      <span v-if="item.type === VALUE_TYPE.SENSOR_GROUP" class="text">
        {{ item.label }}
      </span>
      <span v-else class="wrapper">
        <icon-sensor class="icon" />
        <span class="text">
          {{ item.label }}
        </span>
      </span>
    </template>
  </hierarchical-item-selector>
</template>

<script>
import { HierarchicalItemSelector } from 'ifield-core-components'
import IconSensor from '@/assets/ifield/ic_sensor.svg?component'
import { extractSensorGroupSensorRelation } from './SensorHelpers'
import i18n from '@/i18n.js'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { mapGetters } from 'vuex'

// 値種別コード
const VALUE_TYPE = {
  SENSOR_GROUP: 'sensorGroupId',
  SENSOR: 'sensorId',
}

export default {
  name: 'AppSensorGroupAndSensorSelector',
  components: {
    HierarchicalItemSelector,
    IconSensor,
  },
  props: {
    /**
     * 「すべてのセンサー」を選択している時に表示されるラベル
     */
    selectedAllLabel: {
      type: String,
      default: i18n.t('appSensorGroupAndSensorSelector.allSensors'),
    },
    /**
     * サイズ
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: 'sm',
    },
    /**
     * エレメントIDのprefix
     */
    idPrefix: {
      type: String,
      default: '',
    },
    /**
     * センサーグループ選択値
     */
    sensorGroupIds: {
      type: Array,
      default: null,
    },
    /**
     * センサー選択値
     */
    sensorIds: {
      type: Array,
      default: null,
    },
    /**
     * プレイスID
     */
    placeId: {
      type: Number,
      default: null,
    },
    /**
     * ユーザーID
     */
    userId: {
      type: Number,
      default: null,
    },
  },
  emits: [
    /**
     * 変更イベント
     * @param {Object} value 値
     * @param {string} text 表示テキスト
     */
    'change',
    /**
     * 選択イベント
     * @param {Object} value 値
     * @param {string} text 表示テキスト
     */
    'selected',
  ],
  setup() {
    const { handleApiError } = useHandleApiError()
    return { VALUE_TYPE, handleApiError }
  },
  data: function () {
    return {
      /**
       * 値(ローカル保持用)
       * @type {Object}
       */
      value: null,
    }
  },
  computed: {
    ...mapGetters('sensor', ['sensorMap']),
    ...mapGetters('sensorGroup', ['sensorGroupMap']),
    /**
     * エレメントIDのprefix
     * @type {string}
     */
    modifiedIdPrefix: function () {
      const base = 'sensor-group-sensor'
      return this.idPrefix ? `${this.idPrefix}-${base}` : base
    },
    /**
     * 選択肢になるセンサーデータ
     * @type {Array.<Object>}
     */
    sensors: function () {
      if (this.placeId) {
        return this.$store.getters['sensor/getSensorsByPlaceId'](this.placeId)
      } else if (this.userId) {
        return this.$store.getters['sensor/getSensorsByUserId'](this.userId)
      } else {
        return this.$store.getters['sensor/sensors']
      }
    },
    /**
     * センサーグループとセンサーの関連({ sensorGroupId, sensorIds })
     * @type {Array.<Object>}
     */
    sensorGroupSensorRelation: function () {
      return extractSensorGroupSensorRelation(this.sensors)
    },
    /**
     * 選択肢
     * @type {Array.<Object>}
     */
    options: function () {
      return this.sensorGroupSensorRelation.flatMap(
        ({ sensorGroupId, sensorIds }) => {
          if (sensorGroupId !== null) {
            return this.$_makeSensorGroupOption(sensorGroupId, sensorIds)
          } else {
            return sensorIds.map(this.$_makeSensorOption)
          }
        }
      )
    },
    /**
     * 非活性フラグ
     * @type {Object}
     */
    disabled: function () {
      let selectedChildSensors
      if (this.value) {
        selectedChildSensors = (
          this.value[VALUE_TYPE.SENSOR_GROUP] || []
        ).flatMap((id) => {
          const { sensorIds } =
            this.sensorGroupSensorRelation.find(
              (rel) => rel.sensorGroupId === id
            ) || {}
          return sensorIds || []
        })
      } else {
        selectedChildSensors = this.sensorGroupSensorRelation
          .filter(({ sensorGroupId }) => sensorGroupId)
          .flatMap(({ sensorIds }) => sensorIds)
      }
      return {
        [VALUE_TYPE.SENSOR_GROUP]: [],
        [VALUE_TYPE.SENSOR]: [...new Set(selectedChildSensors)],
      }
    },
  },
  watch: {
    sensorIds: {
      handler: function () {
        this.$_applyToValue()
      },
      immediate: true,
    },
    sensorGroupIds: {
      handler: function () {
        this.$_applyToValue()
      },
      immediate: true,
    },
  },
  created: function () {
    this.$_updateSensors()
    this.$_updateSensorGroups()
  },
  methods: {
    /**
     * 選択値の表示テキスト生成(AppHierarchicalItemSelector への biniging 用)
     * @param {Objcet[]} options 選択肢データ
     * @param {Object} value 選択値
     * @returns {string} 選択値の表示テキスト
     */
    getSelectedText: function (options, value) {
      if (!value) return ''

      const { sensorGroupId, sensorId } = value
      const results = []

      // センサーグループ選択分
      const collectedSensorIdSet = new Set()
      for (const gid of sensorGroupId || []) {
        const group = options.find(
          (o) => o.type === VALUE_TYPE.SENSOR_GROUP && o.id === gid
        )
        if (!group) continue
        if (group.children.length) {
          results.push(
            `${group.label}(${group.children.map((b) => b.label).join(',')})`
          )
          for (const { id } of group.children) {
            collectedSensorIdSet.add(id)
          }
        } else {
          results.push(group.label)
        }
      }

      // センサー選択分
      // センサーグループ選択で表示された分は除く
      const sensorIds = (sensorId || []).filter(
        (id) => !collectedSensorIdSet.has(id)
      )
      for (const sid of sensorIds) {
        const sensor = this.sensorMap.get(sid)
        if (sensor) {
          results.push(sensor.sensor_name)
        }
      }

      return results.join(', ')
    },

    /**
     * センサーデータ更新
     */
    $_updateSensors: async function () {
      try {
        await this.$store.dispatch('sensor/updateSensorsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch sensors. use fetched before.')
        })
      }
    },

    /**
     * センサーグループデータ更新
     */
    $_updateSensorGroups: async function () {
      try {
        await this.$store.dispatch('sensorGroup/updateSensorGroupsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch sensor groups. use fetched before.')
        })
      }
    },

    /**
     * センサーグループの選択肢データ作成
     * @param {number} sensorGroupId センサーグループID
     * @param {Array.<number>} sensorIds グループに紐づくセンサーID配列
     * @returns {Object} センサーグループの選択肢データ
     */
    $_makeSensorGroupOption: function (sensorGroupId, sensorIds) {
      const name =
        this.sensorGroupMap.get(sensorGroupId)?.sensor_group_name ||
        `${sensorGroupId}`
      const childSensors = sensorIds?.map(this.$_makeSensorOption) || []
      return {
        type: VALUE_TYPE.SENSOR_GROUP,
        id: sensorGroupId,
        label: name,
        children: childSensors,
      }
    },

    /**
     * センサーの選択肢データ作成
     * @param {number} sensorId センサーID
     * @returns {Object} センサーの選択肢データ
     */
    $_makeSensorOption: function (sensorId) {
      const name = this.sensorMap.get(sensorId)?.sensor_name || `${sensorId}`
      return {
        type: VALUE_TYPE.SENSOR,
        id: sensorId,
        label: name,
      }
    },

    /**
     * propsの選択値をvalueに反映
     */
    $_applyToValue: function () {
      if (this.sensorIds == null && this.sensorGroupIds == null) {
        this.value = null
      } else {
        // 片方だけ null という選択はできないので null は空配列に置き換える
        this.value = {
          [VALUE_TYPE.SENSOR]:
            this.sensorIds == null ? [] : [...this.sensorIds],
          [VALUE_TYPE.SENSOR_GROUP]:
            this.sensorGroupIds == null ? [] : [...this.sensorGroupIds],
        }
      }
    },

    /**
     * 変更時処理
     * @param {Object} value 変更値
     * @param {string} text 表示テキスト
     */
    $_onChange: function (value, text) {
      this.$emit('change', value, text)
    },

    /**
     * 選択時処理
     * @param {Object} value 選択値
     * @param {string} text 選択値テキスト
     */
    $_onSelected: function (value, text) {
      this.$emit('selected', value, text)
    },
  },
}
</script>

<style lang="scss" scoped>
.text {
  color: $text-color;
  font-size: $font-size-middle;
}

.wrapper {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 4px;
}

.icon {
  flex-shrink: 0;
  fill: $text-color;
}
</style>
